<template>
  <template v-if="community?.id && Number(community.id) === Number(communityId)">
    <div
      ref="scrollable"
      class="page-community-detail scrollable communities"
      :class="{'apply-community-color': subNavSection !== 'members' || !$route.params.id}"
    >
      <CommunityHero
        ref="communityHero"
        :community="community"
        :title="community.name"
        :showSubTitle="true"
        :collapse-hero="!forceExpanded && (subNavSection !== '' || collapseHero)"
        :show-collapse-button="forceExpanded && subNavSection !== ''"
        :is-subsection="subNavSection !== ''"
        @expand="expandHero"
        @collapse="collapseHeroAction"
      >
        <template v-slot:navigation>
          <div class="basic-hero-nav-bar">
            <div class="top-bar__links">
              <ds-button
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === ''}"
                :label="$t('top_bar_overview')"
                @click="viewOverview"
              />
              <ds-button
                v-if="doesUserHaveFullAccessToCommunity"
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === 'members'}"
                :label="$t('community_section_members')"
                @click="viewMembers"
              />
              <ds-button
                v-if="doesUserHaveFullAccessToCommunity"
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === 'actors'}"
                :label="actorsLabel"
                @click="viewActors"
              />
              <ds-button
                v-if="displayCommunityAnnouncements"
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === 'announcements'}"
                :label="announcementsLabel"
                @click="viewAnnouncements"
              />
              <ds-button
                v-if="displayCommunityAnnouncements"
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === 'events'}"
                :label="eventsLabel"
                @click="viewEvents"
              />
              <ds-button
                v-if="displayCommunityChallenges"
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === 'challenges'}"
                :label="challengesLabel"
                @click="viewChallenges"
              />
              <ds-button
                v-if="displayCommunityKnowledgeBase"
                variant="minimal"
                class="second-top-bar__link-item"
                :class="{'second-top-bar__link-active': subNavSection === 'knowledge-base'}"
                :label="knowledgeBaseLabel"
                @click="viewKnowledgeBase"
              />
            </div>
          </div>
        </template>
      </CommunityHero>

      <div
        :class="subNavSection !== '' && !collapseHero && !forceExpanded ? 'mt-24' : subNavSection !== '' && forceExpanded ? 'mt-0' : ''"
        :style="subNavSection === 'actors' && !$route.params.id ? 'flex: 1; min-height: 0' : ''"
      >
        <!-- START OF COMMUNITY OVERVIEW PAGE -->
        <div v-if="subNavSection === ''">
          <div class="tw-container communities-container" style="margin-bottom: 20px;">

            <div class="no-claim-badge alert-warning" v-if="hasJoinedCommunity && !hasCommunityAccessBasedOnAccessLevel(community)">
              <icon name="warning" size="14" fill="white"></icon>
              <span class="no-claim-badge-text">
              {{
                  $t('communities_warning_claim_actor')
                }}
            </span>
              <span class="no-claim-badge-button" @click="showGlobalSearch"> {{
                  $t('communities_warning_claim_actor_cta')
                }}</span>
            </div>

            <div class="row">

              <div class="col-xs-12 col-sm-12 col-md-6" v-if="managers.length > 0">
                <div class="communities-title" v-if="managers.length === 1">{{ $t('community_manager_title') }}</div>
                <div class="communities-title" v-if="managers.length === 2">{{ $t('community_managers_title') }}</div>
                <div class="community-detail__managers min-h-[250px] max-h-[250px]">
                  <div
                    v-for="manager in managers"
                    :key="manager.id"
                    class="community-detail__manager-row tw-flex min-h-[250px] max-h-[250px]"
                  >
                    <div class="manager-avatar" :style="{ backgroundImage: `url(${manager.photoUrl})` }"/>
                    <div class="manager-name">{{ manager.name }} <span class="bg-primary text-white rounded-md p-1 font-normal"
                                                                       v-if="profile.id === manager.id">{{ $t('knowledge_base_you') }}</span></div>
                    <p v-if="manager.bio" class="manager-bio">{{ manager.bio }}</p>
                    <ds-button
                      v-if="manager.id !== $store.getters.userId && manager.name !== 'removed user'"
                      variant="rounded"
                      size="extra-small"
                      :label="$t('community_contact_cta')"
                      @click="startConversationWithManager(manager)"
                    />
                  </div>
                </div>
              </div>

              <div class="col-xs-12 col-sm-12 col-md-6">
                <div style="display: flex">
                  <router-link
                    v-if="doesUserHaveFullAccessToCommunity"
                    :to="`/communities/${community.id}/members`"
                    class="communities-members-link"
                    :class="{'has-action-buttons': isMember}"
                    style="text-decoration: none"
                  >
                    <div class="communities-title">
                      {{
                        $t('community_latest_members')
                      }}
                    </div>
                  </router-link>
                  <div class="communities-title" v-else>
                    {{
                      $t('community_latest_members')
                    }}
                  </div>

                  <div class="communities-title-action-item" v-if="canUserManageMembers">
                    <ds-button
                      icon="plus-big"
                      variant="minimal"
                      @click="displayAddMemberModal"
                    />
                  </div>

                  <div class="communities-title-action-item">
                    <ds-button
                      icon="chart-pie"
                      v-if="isMember"
                      variant="minimal"
                      @click="navigateToDynamicDashboard"
                    />
                  </div>
                </div>

                <div
                  class="community-detail__members min-h-[250px] max-h-[250px]" id="introjs-communities-detailpage"
                >
                  <div v-if="latestMembers.loading" style="margin: auto;">
                    <loading/>
                    <br>
                  </div>

                  <community-detail-members-card
                    v-else-if="latestMembers && latestMembers.data.length > 0"
                    :members="latestMembers.data"
                    :disable-actions="!doesUserHaveFullAccessToCommunity"
                  />
                  <div style="margin: auto;" v-else>
                    {{ $t('community_members_empty_text') }}
                  </div>
                </div>
              </div>
            </div>

            <div class="tw-flex flex-row items-center no-claim-badge alert-warning justify-between" v-if="!hasJoinedCommunity && displayRequestAccessButton">
              <div>
                <icon name="users" size="14" fill="black"></icon>
                <span class="no-claim-badge-text font-bold text-[16px]">
              {{
                    $t('communities_no_member_request_access')
                  }}
            </span>
              </div>
              <DsButton class="no-claim-badge-button !bg-orange-400" :label="requestAccessButtonLabel" variant="rounded" :disabled="hasUserRequestedAccessToCommunity"
                        @click="requestAccessToTheCommunity"></DsButton>
            </div>

            <CommunityDetailLastCommunityItems
              v-if="areAnnouncementsEnabledForCommunity && community.id"
              :community="community"
            />

          </div>
        </div>
        <!-- END OF DETAIL OVERVIEW PAGE -->

        <template v-if="subNavSection === 'members'">
          <CommunityMembers/>
        </template>

        <!-- TODO: this should either be a generic access denied component, but actually, should not be navigable in the first place + it's either showing the denied component OR the simplified page, not both at the same time -->
        <!--<community-detail-members-access-denied
          v-if="manager && !hasAccessToMembers"
          :manager="manager"
        />-->
        <template v-if="subNavSection === 'actors' && !$route.params.id">
          <loading v-if="loadingCommunity"/>
          <ActorsSimplified
            v-if="doesUserHaveFullAccessToCommunity"
            :card-class="'community-simplified-image-card'"
            :can-add-actors="canAddActorsToCommunityPortfolio"
            :portfolio-id="community.portfolioId"
            :enable-banner="false"
            style="min-height: 0"
          />
          <ActorDetail v-else/>
        </template>
        <div v-if="subNavSection === 'actors' && $route.params.id">
          <loading v-if="loadingCommunity"/>
          <SimplifiedActorDetail v-if="doesUserHaveFullAccessToCommunity && (!this.$store.getters.isOwner && !this.$store.getters.isMember)"/>
          <ActorDetail v-else/>
        </div>

        <!-- Announcements and Events handle their own scrolling -->
        <template v-if="displayAnnouncements">
          <AnnouncementsNew :allow-create-announcement="canCreateAnnouncement"/>
        </template>

        <template v-if="displayEvents">
          <EventsNew :allow-create-announcement="canCreateAnnouncement"/>
        </template>

        <template v-if="subNavSection === 'announcements' && $route.params.id && $route.params.communityId">
          <announcement-detail-simplified-new/>
        </template>

        <template v-if="subNavSection === 'events' && $route.params.id && $route.params.communityId">
          <announcement-detail-simplified-new/>
        </template>

        <div v-if="subNavSection === 'knowledge-base'" class="scrollable" style="min-height: 70vh">
          <KnowledgeBaseChooser/>
        </div>
        <div v-if="subNavSection === 'challenges' && !$route.params.id" class="scrollable" style="min-height: 70vh">
          <Challenges/>
        </div>
        <div
          v-if="subNavSection === 'challenges' && $route.params.id && $route.params.communityId" class="scrollable"
          style="min-height: 70vh"
        >
          <ChallengeDetailNew/>
        </div>

        <SimplifiedFooter v-if="subNavSection === ''"/>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="page-community-detail scrollable communities apply-community-color" style="padding-top: 75px;">
      <Loading/>
    </div>
  </template>
</template>

<script lang="ts">
  import CommunityHero from '../../components/Simplified/CommunityHero.vue'
  import BasicHero from '../../components/BasicHero/BasicHero.vue'
  import ActorsSimplified from '../Simplified/ActorsSimplified.vue'
  import AnnouncementsNew from '../Announcements.vue'
  import Avatar from '../../components/Avatar/Avatar.vue'
  import Loading from '../../components/Dashboard/ConceptMap/Loading.vue'
  import SimplifiedImageCard from '../../components/Connection/SimplifiedImageCard.vue'
  import CommunityDetailMembersCard from '../../components/Communities/CommunityDetailMembersCard.vue'
  import KnowledgeBaseSimplified from '../../components/Dashboard/KnowledgeBaseSimplified.vue'
  import TranslationsMixin from '../../util/TranslationsMixin.js'
  import { getCardsPerCarouselPageBasedOnWindowWidth } from '../../util/helpers.js'

  import CommunityDetailManagerCard from '../../components/Communities/CommunityDetailManagerCard.vue'
  import { Conversations } from '@/api/conversations'
  import { ACTION_TYPES as CONVERSATION_ACTION_TYPES } from '@/store/modules/conversations'
  import CommunitiesCard from '../../components/Communities/CommunitiesCard.vue'
  import { MUTATION_TYPES as FILTERS_MUTATION_TYPES } from '../../store/modules/filters.js'
  import { ACTION_TYPES as COMMUNITY_ACTION_TYPES } from '../../store/modules/communities.js'
  import { MUTATION_TYPES as NOTIFICATION_MUTATION_TYPES } from '../../store/modules/notifications.js'
  import SimplifiedActorDetail from '../ActorDetail/SimplifiedActorDetail.vue'
  import NewCard from '../../components/NewCard/NewCard.vue'
  import CommunityDetailMembersAccessDenied from '../../components/Communities/CommunityDetailMembersAccessDenied.vue'
  import AnnouncementDetailSimplifiedNew from '../Simplified/AnnouncementDetailSimplifiedNew.vue'
  import SimplifiedAnnouncementPreview from '../../components/Simplified/SimplifiedAnnouncementPreview.vue'
  import SimplifiedFooter from '../../components/SimplifiedFooter/SimplifiedFooter.vue'
  import EventCard from '../../components/Simplified/EventCard.vue'
  import { mix } from 'polished'
  import IntroJsMixin from '../../util/IntroJsMixin.js'
  import { MUTATION_TYPES as KNOWLEDGE_BASE_MUTATION_TYPES } from '../../store/modules/knowledgeBase.js'
  import { MUTATION_TYPES as UI_MUTATION_TYPES } from '../../store/modules/ui.js'
  import MODAL_IDS from '../../constants/modal-ids.js'
  import { fetchLatestMembers, requestAccessToCommunity } from '@/api/communities.js'
  import EventsNew from '../Events.vue'
  import { defineComponent } from 'vue'
  import Challenges from '../Challenges.vue'
  import ChallengeDetail from '@/pages/Simplified/ChallengeDetail.vue'
  import CommunityMixin from '@/util/CommunityMixin'
  import CommunityMembers from '@/pages/CommunityMembers.vue'
  import ChallengeCard from '@/components/Simplified/ChallengeCard.vue'
  import CommunityDetailLastCommunityItems from '@/components/Communities/CommunityDetailLastCommunityItems.vue'
  import { CommunityDetail } from '@/Domain/Community/types'
  import { UserBaseInfoData } from '@/Domain/User/Types/types'
  import ActorDetail from '@/pages/ActorDetail/ActorDetail.vue'
  import ChallengeDetailNew from '@/pages/Simplified/ChallengeDetailNew.vue'
  import KnowledgeBase from '@/Domain/KnowledgeBase/Components/KnowledgeBase.vue'
  import KnowledgeBaseChooser from '@/components/Dashboard/KnowledgeBaseChooser.vue'
  import { ACTION_TYPES } from '@/store/modules/user'
  import _debounce from 'lodash/debounce.js'
  import AvatarNew from '@/components/Avatar/AvatarNew.vue'
  import UserMixin from '@/util/UserMixin'

  export default defineComponent({
    components: {
      AvatarNew,
      CommunityHero,
      KnowledgeBaseChooser,
      KnowledgeBase,
      ChallengeDetailNew,
      ActorDetail,
      CommunityDetailLastCommunityItems,
      ChallengeCard,
      CommunityMembers,
      ChallengeDetail,
      Challenges, EventsNew,
      SimplifiedFooter,
      CommunityDetailMembersAccessDenied,
      NewCard,
      CommunitiesCard,
      CommunityDetailManagerCard,
      CommunityDetailMembersCard,
      ActorsSimplified,
      BasicHero,
      AnnouncementsNew,
      Avatar,
      Loading,
      SimplifiedImageCard,
      SimplifiedActorDetail,
      AnnouncementDetailSimplifiedNew,
      EventCard,
      SimplifiedAnnouncementPreview,
      KnowledgeBaseSimplified,
    },
    data() {
      return {
        recentAnnouncements: {
          data: [],
          isLoading: false,
        },
        upcomingEvents: {
          data: [],
          isLoading: false,
        },
        latestMembers: {
          data: [],
          isLoading: false,
        },
        latestChallenges: [],
        cardsPerCarouselPage: getCardsPerCarouselPageBasedOnWindowWidth(window.innerWidth),
        collapseHero: false,
        collapseHeight: 0,
        forceExpanded: false,
      }
    },
    computed: {
      requestAccessButtonLabel(): string {
        if (this.hasUserRequestedAccessToCommunity) {
          return this.$t('community_requested_access')
        }

        return this.$t('community_request_access')
      },
      canAddActorsToCommunityPortfolio(): boolean {
        return this.community.portfolioId != null && (this.isMember || this.isLoggedInUserCommunityManager)
      },
      areAnnouncementsEnabledForCommunity(): boolean {
        return this.areMessageBoardsEnabled && this.hasAccessToAnnouncementsAndEventsForCommunities
      },
      displayEvents(): boolean {
        return this.areMessageBoardsEnabled
          && this.hasAccessToAnnouncementsAndEventsForCommunities
          && this.subNavSection === 'events'
          && !this.$route.params.id
      },
      displayAnnouncements(): boolean {
        return this.areAnnouncementsEnabledForCommunity
          && this.hasAccessToAnnouncementsAndEventsForCommunities
          && this.subNavSection === 'announcements'
          && !this.$route.params.id
      },
      loadingCommunity(): boolean {
        return this.$store.state.communities.detail.loading
      },
      community(): CommunityDetail | null {
        return this.$store.state.communities.detail.data
      },
      managers(): UserBaseInfoData[] {
        return this.community?.managersData ?? []
      },
      isLoggedIn(): boolean {
        return this.$store.getters.isLoggedIn
      },
      isMember(): boolean {
        return this.$store.getters.isMember
      },
      hasAccessToMembers(): boolean {
        if (!this.community) {
          return false
        }

        if (this.isMember) {
          return true
        }

        return this.joinedCommunities.filter((c) => c.id === this.community?.id).length > 0
      },
      isLoadingChallenges() {
        return this.$store.state.challenges.listData.loading
      },
      areMessageBoardsEnabled(): boolean {
        return this.$store.getters.areMessageBoardsEnabled
      },
      hasAccessToAnnouncementsAndEventsForCommunities(): boolean {
        return this.$store.getters.hasAccessToAnnouncementsAndEventsForCommunities
      },
      communityId(): number {
        return Number(this.$route.params.communityId)
      },
      subNavSection(): string {
        return this.$route.params.section
      },
      config(): any {
        return this.$store.state.config
      },
      hasJoinedCommunity() {
        return this.$store.getters.joinedCommunityIds.includes(this.communityId)
      },
    },
    watch: {
      collapseHero(newValue, oldValue) {
        // When the hero collapses/expands, any dynamic content may need to adjust
        this.$nextTick(() => {
          // Additional logic when collapse state changes if needed
        })
      },
    },
    methods: {
      startConversationWithManager(manager) {
        if (!this.isLoggedIn) {
          return
        }
        Conversations
          .post({ recipient_id: manager.id })
          .then((conversation) => {
            this.$store.dispatch(CONVERSATION_ACTION_TYPES.FETCH_CONVERSATIONS_LIST)
            this.$router.push('/profile/conversations/' + conversation.id)
          })
      },
      viewOverview() {
        this.$router.push(`/communities/${this.communityId}`)
      },
      viewMembers() {
        this.$router.push(`/communities/${this.communityId}/members`)
      },
      viewActors() {
        this.$router.push(`/communities/${this.communityId}/actors`)
      },
      viewAnnouncements() {
        this.$router.push(`/communities/${this.communityId}/announcements`)
      },
      viewEvents() {
        this.$router.push(`/communities/${this.communityId}/events`)
      },
      viewKnowledgeBase() {
        this.$router.push(`/communities/${this.communityId}/knowledge-base`)
      },
      viewChallenges() {
        this.$router.push(`/communities/${this.communityId}/challenges`)
      },
      expandHero() {
        // Force expand the hero and scroll to top
        this.forceExpanded = true
        this.collapseHero = false
        this.collapseHeight = 0
        if (this.$refs.scrollable) {
          this.$refs.scrollable.scrollTo({ top: 0, behavior: 'smooth' })
        }
      },
      collapseHeroAction() {
        // Collapse the hero back (only used on subsections)
        this.forceExpanded = false
      },
      requestAccessToTheCommunity() {
        if (this.hasUserRequestedAccessToCommunity) {
          return
        }

        if (!this.isLoggedIn) {
          this.$router.push('/login?redirect_uri=/communities/' + this.communityId)
          return
        }

        requestAccessToCommunity(this.community.id)
          .then(() => {
            this.$store.dispatch(ACTION_TYPES.REFRESH_PROFILE)
          })
          .catch(error => {
            console.log(error)
          })
      },
      showGlobalSearch() {
        this.$bus.emit('showGlobalSearch', { query: '' })
      },
      navigateToDynamicDashboard() {
        this.$router.push(`/settings/ecosystem-heartbeat?community=${this.communityId}`)
      },
      async loadContent() {
        try {
          await this.$store.dispatch(COMMUNITY_ACTION_TYPES.FETCH_COMMUNITY_DETAIL, Number(this.communityId))

          this.$store.commit(FILTERS_MUTATION_TYPES.SET_PORTFOLIO, this.community.portfolioId)
        } catch (exception) {
          console.log(exception)

          return this.$router.push('/communities')
        }

        if (this.community && this.community.color && !this.$store.state.communities.detail.loading) {
          this.setCommunityColor(this.community.color)
        }

        this.fetchLatestMembers()
      },
      displayAddMemberModal() {
        this.$store.commit(UI_MUTATION_TYPES.SET_MODAL_CONTEXT, {
          community: {
            name: this.community.name,
            id: this.communityId,
          },
        })
        this.$store.commit(UI_MUTATION_TYPES.SHOW_MODAL, MODAL_IDS.ADD_MEMBER_TO_COMMUNITY_MODAL)
      },
      async fetchLatestMembers() {
        this.latestMembers.isLoading = true

        fetchLatestMembers({ communityId: this.communityId })
          .then(items => {
            this.latestMembers.loading = false
            this.latestMembers.data = items
          })
          .catch(error => {
            this.latestMembers.loading = false
            console.log(error)
          })
      },
      setCommunityColor(color) {
        const communityDark = mix(0.9, color, '#000')
        const communityDarker = mix(0.75, color, '#000')
        const communityDarkest = mix(0.5, color, '#000')
        const communityExtraDarkest = mix(0.25, color, '#000')
        const communityLight = mix(0.9 * 100 / 255, color, '#fff')
        const communityLighter = mix(0.75 * 100 / 255, color, '#fff')
        const communityLightest = mix(0.5 * 100 / 255, color, '#fff')
        const communityExtraLightest = mix(0.25 * 100 / 255, color, '#fff')
        document.documentElement.style.setProperty('--primary-community', color)
        document.documentElement.style.setProperty('--primary-community-dark', communityDark)
        document.documentElement.style.setProperty('--primary-community-darker', communityDarker)
        document.documentElement.style.setProperty('--primary-community-darkest', communityDarkest)
        document.documentElement.style.setProperty('--primary-community-extra-darkest', communityExtraDarkest)
        document.documentElement.style.setProperty('--primary-community-light', communityLight)
        document.documentElement.style.setProperty('--primary-community-lighter', communityLighter)
        document.documentElement.style.setProperty('--primary-community-lightest', communityLightest)
        document.documentElement.style.setProperty('--primary-community-extra-lightest', communityExtraLightest)
      },
      startIntroJs() {
        if (this.seenIntros.includes('communitiesDetailPage')) {
          return
        }
        const config = this.buildIntroJsConfig(this.config, this.$store.getters.userRole)
        if (!config || !config.communitiesDetailPage || !config.communitiesDetailPage.steps) {
          return
        }

        config.communitiesDetailPage.steps = this.getIntroJsStepsConfig(config.communitiesDetailPage.steps)
        const intro = this.introJs().setOptions(config.communitiesDetailPage)
        this.currentIntro = intro

        const componentScope = this
        intro.onbeforechange(this.introJsBeforeStepCallback.bind(this))
        intro.onafterchange(this.introJsAfterStepCallback.bind(this))
        intro.oncomplete(function () {
          componentScope.introJsOnCompleteCallback(this._options, this._currentStep, componentScope)
        })
        intro.onexit(function () {
          componentScope.introJsOnCompleteCallback(this._options, this._currentStep, componentScope)
        })

        intro.start()
      },
      emitScrollEvent: _debounce(function (e) {
        this.$bus.emit('scrollToEndOfCommunityPage')
      }, 200),
      onScroll(e) {
        /*if ((e.target.scrollTop + e.target.offsetHeight + 1000) > e.target.scrollHeight) {
          this.emitScrollEvent()
        }*/
        if (!this.$refs.scrollable || !this.$refs.communityHero || !this.$refs.communityHero.$el) {
          return
        }

        const scrollHeight = this.$refs.scrollable.scrollTop
        const headerHeight = this.$refs.communityHero.$el.clientHeight
        const navHeight = 0

        // Reset forceExpanded when user scrolls down past the header
        if (this.forceExpanded && scrollHeight >= (headerHeight - navHeight)) {
          this.forceExpanded = false
        }

        if (this.collapseHero) {
          if ((scrollHeight - this.collapseHeight + navHeight) >= 0) {
            this.collapseHero = true
          } else {
            this.collapseHero = false
            this.collapseHeight = 0
          }
          return
        }

        this.collapseHero = scrollHeight >= (headerHeight - navHeight)
        this.collapseHeight = headerHeight
      },
    },
    async mounted() {
      await this.loadContent()

      // Enforce the correct route based on the setting of announcements and events
      if (['announcements', 'events'].includes(this.subNavSection) && !this.$route.params.id && !this.hasAccessToAnnouncementsAndEventsForCommunities) {
        this.$router.push('/communities/' + this.$route.params.communityId)
      }

      // Add scroll listener
      if (this.$refs.scrollable) {
        //this.$refs.scrollable.addEventListener('scroll', this.onScroll, { passive: true })
      }

      // this setTimeout is used because the startIntrojs loaded faster than the content on the page when we use clickAfter
      setTimeout(() => {
        this.startIntroJs()
      })
    },
    created() {
      this.$bus.on('invited-community-member', () => {
        this.fetchLatestMembers()
      })
    },
    beforeUnmount() {
      // Remove scroll listener
      if (this.$refs.scrollable) {
        this.$refs.scrollable.removeEventListener('scroll', this.onScroll, { passive: true })
      }

      this.$bus.off('invited-community-member')
      this.$bus.off('announcementCreated')

      this.$store.dispatch(COMMUNITY_ACTION_TYPES.RESET_COMMUNITY_DETAIL)
      this.$store.commit(FILTERS_MUTATION_TYPES.CLEAR)
      this.$store.commit(KNOWLEDGE_BASE_MUTATION_TYPES.CLEAR)
      this.$store.commit(NOTIFICATION_MUTATION_TYPES.RESET_ACTIVE_DEFAULT_FILTER)
      this.$store.commit(NOTIFICATION_MUTATION_TYPES.RESET_ACTIVE_TAGS)
      this.setCommunityColor(this.$store.state.config.primaryColor)
    },
    mixins: [TranslationsMixin, IntroJsMixin, CommunityMixin, UserMixin]
  })
</script>

<style lang="scss" scoped>
  @import "../../../scss/variables";

  .page-community-detail {
    height: 100%;
    display: flex;
    flex-direction: column;

    .alert-warning {
      border: 1px solid #ff805a;
      background-color: white;
      color: black;
    }

    .no-claim-badge {
      margin-top: 24px;
      border-radius: 12px;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
      width: 100%;
      display: flex;
      flex-wrap: wrap;
      padding: 20px;

      .no-claim-badge-text {
        margin-left: 5px;
      }

      .no-claim-badge-button {
        margin-left: 5px;
        text-decoration: underline;
        cursor: pointer;
      }
    }

    .community-detail__sub-nav {
      .active {
        text-decoration: underline;
      }
    }

    h1.h1 {
      font-size: 26px;
      text-transform: uppercase;
      font-family: $font-stack-secondary;
      font-weight: 200;
    }
  }

  .filters {
    overflow: hidden;
    overflow-y: auto;
    padding: 1.5rem 2rem;

    @media (max-width: $screen-md) {
      padding: 1.5rem;
    }
  }

  .communities-members-link {
    text-decoration: none;
    display: block;

    &.has-action-buttons {
      width: calc(100% - 36px);
    }
  }

  .filters-title-container {
    display: flex;
    align-items: center;
  }

  .community-detail__managers {
    width: 100%;
    min-height: 230px;
    display: flex;
    flex-direction: column;
    gap: 12px;

    @media (min-width: $screen-sm) {
      flex-direction: row;
    }

    .community-detail__manager-row {
      flex: 1;
      flex-direction: column;
      justify-content: center;
      align-items: center !important;
      gap: 8px !important;
      padding: 20px !important;
      text-align: center;
      border-radius: $default-border-radius;
      box-shadow: $simplified-box-shadow-config;
      background: white;

      .manager-avatar {
        width: 64px;
        height: 64px;
        border-radius: 50%;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        border: 2px solid #f3f4f6;
        flex-shrink: 0;
      }

      .manager-name {
        font-weight: 600;
        font-size: 0.875rem;
        color: #111827;
      }

      .manager-bio {
        overflow: hidden;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 3;
        margin: 0;
        font-size: 0.75rem;
        color: #6b7280;
      }
    }
  }

  .communities-detail__empty {
    font-size: 14px;
    text-align: center;
    line-height: 85px;
    margin: auto;
  }
</style>
